import { Controller } from "@hotwired/stimulus"

// Full constraint matrix — synced from Meta::CampaignCreator::GOAL_MATRIX
// Each objective defines: platforms, goals (with requirements), kpi label
const GOAL_MATRIX = {
  OUTCOME_AWARENESS: {
    label: "Awareness", description: "Maximize visibility",
    platforms: ["facebook", "instagram"],
    goals: {
      "Reach":       { requires: [], hint: "Show ads to maximum unique people" },
      "Impressions": { requires: [], hint: "Maximize total ad impressions" }
    },
    kpi: "Target CPM", unit: "€", kind: "currency"
  },
  OUTCOME_ENGAGEMENT: {
    label: "Engagement", description: "Interactions and video views",
    platforms: ["facebook", "instagram"],
    goals: {
      "Post Engagement": { requires: [], hint: "Likes, comments, shares and saves" },
      "Video Views":     { requires: ["video"], hint: "ThruPlay — requires video or reel content" }
    },
    kpi: "Target CPE", unit: "€", kind: "currency"
  },
  OUTCOME_TRAFFIC: {
    label: "Traffic", description: "Clicks to your website",
    platforms: ["facebook", "instagram"],
    goals: {
      "Link Clicks":        { requires: ["url"], hint: "Maximize clicks to your destination" },
      "Landing Page Views":  { requires: ["url", "pixel"], hint: "Optimize for people who load the page (needs pixel)" }
    },
    kpi: "Target CPC", unit: "€", kind: "currency"
  },
  OUTCOME_SALES: {
    label: "Sales", description: "Conversions and ROAS",
    platforms: ["facebook", "instagram"],
    goals: {
      "Purchases":         { requires: ["pixel", "url"], hint: "Optimize for purchase events" },
      "Add to Cart":       { requires: ["pixel", "url"], hint: "Optimize for add-to-cart events" },
      "Initiate Checkout":  { requires: ["pixel", "url"], hint: "Optimize for checkout initiation" },
      "ROAS":              { requires: ["pixel", "url"], hint: "Optimize for return on ad spend" }
    },
    kpi: "Target ROAS", unit: "×", kind: "ratio"
  },
  OUTCOME_LEADS: {
    label: "Leads", description: "Generate prospects",
    platforms: ["facebook", "instagram"],
    goals: {
      "Form Submissions": { requires: ["pixel"], hint: "Optimize for lead form completions" },
      "Registrations":    { requires: ["pixel"], hint: "Optimize for sign-up/registration events" }
    },
    kpi: "Target CPA", unit: "€", kind: "currency"
  }
}

const REQUIREMENT_LABELS = {
  pixel: "Requires conversion pixel",
  url: "Requires website URL (set in Customize step)",
  video: "Requires video or reel post types"
}

export default class extends Controller {
  static targets = [
    "objectiveCard", "linkWarning", "pixelSection", "pixelSelect", "eventSelect",
    "pixelError", "pixelBlocked",
    "goalSection", "goalPills", "performanceGoalInput",
    "kpiSection", "kpiLabel", "kpiUnit", "kpiInput", "kpiCentsInput", "kpiRoasInput",
    "campaignName", "adsetName", "budgetInput", "budgetCentsInput",
    "countryTags", "countrySelect", "countriesInput",
    "placementGrid", "placementHelp"
  ]

  static values = {
    pageType: String,
    objective: String,
    performanceGoal: String,
    budgetType: String,
    postTypes: { type: Array, default: [] }
  }

  connect() {
    console.log("[campaign-creator] connect: objective =", this.objectiveValue || "(empty)", "pageType =", this.pageTypeValue)

    this.updateObjectiveCards()

    if (this.objectiveValue) {
      this.updateUIForObjective(this.objectiveValue)
    }
  }

  objectiveChanged(event) {
    const objective = event.target.value
    console.log("[campaign-creator] objectiveChanged:", objective)
    this.objectiveValue = objective

    this.updateObjectiveCards()
    this.updateUIForObjective(objective)
    this.updateNames(objective)

    // Clear performance goal when objective changes
    this.performanceGoalValue = ""
    if (this.hasPerformanceGoalInputTarget) {
      this.performanceGoalInputTarget.value = ""
    }

    this.dispatch("changed")
  }

  // Gray out objectives that don't support the current platform
  updateObjectiveCards() {
    const platform = this.pageTypeValue
    if (!platform) return

    this.objectiveCardTargets.forEach(card => {
      const objective = card.dataset.objective
      const config = GOAL_MATRIX[objective]
      const selected = this.objectiveValue === objective
      const supported = config && config.platforms.includes(platform)

      // Reset classes
      card.classList.remove("border-brand", "bg-blue-50", "border-gray-200", "opacity-50", "pointer-events-none")

      if (!supported) {
        card.classList.add("border-gray-200", "opacity-50", "pointer-events-none")
        const radio = card.querySelector("input[type=radio]")
        if (radio) radio.disabled = true
      } else if (selected) {
        card.classList.add("border-brand", "bg-blue-50")
        const radio = card.querySelector("input[type=radio]")
        if (radio) radio.disabled = false
      } else {
        card.classList.add("border-gray-200")
        const radio = card.querySelector("input[type=radio]")
        if (radio) radio.disabled = false
      }
    })
  }

  updateUIForObjective(objective) {
    const config = GOAL_MATRIX[objective]
    if (!config) {
      console.warn("[campaign-creator] unknown objective:", objective)
      return
    }
    console.log("[campaign-creator] updateUI for:", objective, "platform:", this.pageTypeValue)

    // Link warning (Facebook + link-requiring objectives)
    if (this.hasLinkWarningTarget) {
      const needsLink = this.objectiveNeedsUrl(objective)
      if (needsLink && this.pageTypeValue === "facebook") {
        this.linkWarningTarget.classList.remove("hidden")
      } else {
        this.linkWarningTarget.classList.add("hidden")
      }
    }

    // Pixel section — show if any goal requires pixel
    if (this.hasPixelSectionTarget) {
      const anyNeedsPixel = Object.values(config.goals).some(g => g.requires.includes("pixel"))
      if (anyNeedsPixel) {
        this.pixelSectionTarget.classList.remove("hidden")
        this.loadPixels()
        // Re-run the pixel access check for the new objective (e.g. switching
        // INTO Sales with no pixel selected should surface the requirement).
        this.checkPixelAccess()
      } else {
        this.pixelSectionTarget.classList.add("hidden")
        this._setPixelBlock(false, null)
      }
    }

    // Performance goals — render with constraint indicators
    if (this.hasGoalSectionTarget) {
      this.goalSectionTarget.classList.remove("hidden")
      this.renderGoalPills(config.goals, objective)
    }

    // KPI
    if (this.hasKpiSectionTarget) {
      this.kpiSectionTarget.classList.remove("hidden")
      if (this.hasKpiLabelTarget) this.kpiLabelTarget.textContent = config.kpi
      const kind = config.kind || "currency"
      if (this.hasKpiUnitTarget) this.kpiUnitTarget.textContent = config.unit || "€"
      if (this.hasKpiInputTarget) {
        this.kpiInputTarget.step = kind === "ratio" ? "0.1" : "0.01"
        this.kpiInputTarget.placeholder = kind === "ratio" ? "0.0" : "0.00"
      }
      // Re-route the current visible value to the hidden field that matches
      // this objective's KPI kind (and clear the other), so switching
      // objectives keeps the submitted fields consistent.
      this.kpiInputChanged()
    }
  }

  objectiveNeedsUrl(objective) {
    const config = GOAL_MATRIX[objective]
    if (!config) return false
    return Object.values(config.goals).some(g => g.requires.includes("url"))
  }

  renderGoalPills(goals, objective) {
    if (!this.hasGoalPillsTarget) return
    const container = this.goalPillsTarget
    container.innerHTML = ""

    const platform = this.pageTypeValue
    const postTypes = this.postTypesValue || []

    Object.entries(goals).forEach(([goalName, goalConfig]) => {
      const wrapper = document.createElement("div")
      wrapper.className = "flex flex-col items-start"

      // Check if requirements are met
      const unmet = this.getUnmetRequirements(goalConfig.requires, postTypes)
      const isDisabled = unmet.length > 0
      const isSelected = this.performanceGoalValue === goalName

      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "px-4 py-2 rounded-full border text-sm font-medium transition-all "

      if (isDisabled) {
        btn.className += "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed opacity-60"
        btn.disabled = true
      } else if (isSelected) {
        btn.className += "bg-brand text-white border-brand cursor-pointer"
      } else {
        btn.className += "bg-white text-gray-700 border-gray-200 hover:border-gray-300 cursor-pointer"
      }

      // Goal name + requirement icons
      let labelHtml = goalName
      if (goalConfig.requires.length > 0) {
        const icons = goalConfig.requires.map(r => {
          const met = !unmet.includes(r)
          if (r === "pixel") return `<span class="${met ? 'text-green' : 'text-amber-500'}" title="${REQUIREMENT_LABELS[r]}">◉</span>`
          if (r === "url") return `<span class="${met ? 'text-green' : 'text-amber-500'}" title="${REQUIREMENT_LABELS[r]}">🔗</span>`
          if (r === "video") return `<span class="${met ? 'text-green' : 'text-amber-500'}" title="${REQUIREMENT_LABELS[r]}">▶</span>`
          return ""
        }).join(" ")
        labelHtml += ` <span class="text-xs ml-1">${icons}</span>`
      }
      btn.innerHTML = labelHtml

      if (!isDisabled) {
        btn.addEventListener("click", () => this.selectGoal(goalName, container))
      }

      wrapper.appendChild(btn)

      // Hint text
      if (goalConfig.hint) {
        const hint = document.createElement("p")
        hint.className = "text-xs mt-1 ml-1 " + (isDisabled ? "text-amber-600" : "text-gray-400")
        hint.textContent = isDisabled ? unmet.map(r => REQUIREMENT_LABELS[r]).join(". ") : goalConfig.hint
        wrapper.appendChild(hint)
      }

      container.appendChild(wrapper)
    })
  }

  getUnmetRequirements(requires, postTypes) {
    const unmet = []

    requires.forEach(req => {
      if (req === "video") {
        // Video required but no video post types selected
        const hasVideo = postTypes.some(t => ["video", "reels", "carousel_album"].includes(t))
        if (!hasVideo && postTypes.length > 0) {
          unmet.push("video")
        }
        // If no post types selected yet, don't block — they might add video later
      }
      // pixel and url can't be checked from Step 2 — just show as info indicator
      // They'll be validated at finalize time
    })

    return unmet
  }

  selectGoal(goal, container) {
    console.log("[campaign-creator] selectGoal:", goal)
    this.performanceGoalValue = goal
    if (this.hasPerformanceGoalInputTarget) {
      this.performanceGoalInputTarget.value = goal
    }

    // Re-render to update styling
    const objective = this.objectiveValue
    const config = GOAL_MATRIX[objective]
    if (config) {
      this.renderGoalPills(config.goals, objective)
    }

    this.dispatch("changed")
  }

  updateNames(objective) {
    const short = objective.replace("OUTCOME_", "").toLowerCase()
    const date = new Date().toISOString().split("T")[0]

    if (this.hasCampaignNameTarget) {
      this.campaignNameTarget.value = `AB-${short}-${date}`
    }
    if (this.hasAdsetNameTarget) {
      this.adsetNameTarget.value = `AB-broad-${short}-${date}`
    }
  }

  budgetTypeChanged(event) {
    console.log("[campaign-creator] budgetType:", event.target.value)
    this.dispatch("changed")
  }

  // Budget: convert euros to cents
  budgetInputChanged() {
    if (this.hasBudgetInputTarget && this.hasBudgetCentsInputTarget) {
      const euros = parseFloat(this.budgetInputTarget.value) || 0
      this.budgetCentsInputTarget.value = Math.round(euros * 100)
    }
  }

  // KPI target: route the visible value to the hidden field matching the
  // current objective's KPI kind. Currency KPIs (CPM/CPE/CPC/CPA) store
  // cents (× 100); the ROAS KPI stores a raw ratio multiplier. Only one
  // hidden field is ever populated — the other is cleared.
  kpiInputChanged() {
    if (!this.hasKpiInputTarget) return
    const config = GOAL_MATRIX[this.objectiveValue]
    const kind = config?.kind || "currency"
    const raw = parseFloat(this.kpiInputTarget.value)
    const hasValue = !isNaN(raw) && this.kpiInputTarget.value !== ""

    if (kind === "ratio") {
      if (this.hasKpiRoasInputTarget) this.kpiRoasInputTarget.value = hasValue ? raw : ""
      if (this.hasKpiCentsInputTarget) this.kpiCentsInputTarget.value = ""
    } else {
      if (this.hasKpiCentsInputTarget) this.kpiCentsInputTarget.value = hasValue ? Math.round(raw * 100) : ""
      if (this.hasKpiRoasInputTarget) this.kpiRoasInputTarget.value = ""
    }
  }

  // Country management
  addCountry(event) {
    const code = event.target.value
    if (!code) return
    console.log("[campaign-creator] addCountry:", code)

    const countries = this.getCountries()
    if (countries.includes(code)) return

    countries.push(code)
    this.setCountries(countries)
    event.target.value = ""

    // Add tag chip
    const tag = document.createElement("span")
    tag.className = "inline-flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 rounded-full text-sm"
    tag.innerHTML = `${code} <button type="button" class="font-bold hover:text-red-600 cursor-pointer" data-action="click->campaign-creator#removeCountry" data-country="${code}">×</button>`
    this.countryTagsTarget.appendChild(tag)

    this.dispatch("changed")
  }

  removeCountry(event) {
    const code = event.currentTarget.dataset.country
    console.log("[campaign-creator] removeCountry:", code)

    const countries = this.getCountries().filter(c => c !== code)
    this.setCountries(countries)

    // Remove tag
    event.currentTarget.closest("span").remove()
    this.dispatch("changed")
  }

  getCountries() {
    try {
      return JSON.parse(this.countriesInputTarget.value || "[]")
    } catch {
      return []
    }
  }

  setCountries(countries) {
    this.countriesInputTarget.value = JSON.stringify(countries)
  }

  // Placements
  placementModeChanged(event) {
    const mode = event.target.value
    console.log("[campaign-creator] placementMode:", mode)

    if (this.hasPlacementGridTarget) {
      if (mode === "manual") {
        this.placementGridTarget.classList.remove("hidden")
        if (this.hasPlacementHelpTarget) {
          this.placementHelpTarget.textContent = "Select which placements to use for your ads."
        }
      } else {
        this.placementGridTarget.classList.add("hidden")
        if (this.hasPlacementHelpTarget) {
          this.placementHelpTarget.textContent = "Meta will automatically show your ads in the best-performing placements."
        }
      }
    }
  }

  ageChanged() {
    this.dispatch("changed")
  }

  pixelChanged() {
    console.log("[campaign-creator] pixelChanged:", this.pixelSelectTarget.value)
    this.checkPixelAccess()
  }

  // Inline pre-save check: ask the server whether the selected pixel is
  // accessible (reads scanned MetaAssetPermission — no live Meta call) and
  // surface the result under the field. Sets a hidden `pixelBlocked` flag the
  // wizard-footer reads, so a doomed campaign-provision is blocked BEFORE the
  // destructive save (AB-062 follow-up — the vanishing ROAS toast). Only runs
  // for pixel-required objectives (Sales / Leads).
  async checkPixelAccess() {
    const requiresPixel = this.objectiveNeedsPixel(this.objectiveValue)
    const pixelId = this.hasPixelSelectTarget ? this.pixelSelectTarget.value : ""

    // Not a pixel objective → never blocks; clear any prior state.
    if (!requiresPixel) { this._setPixelBlock(false, null); this.dispatch("changed"); return }

    try {
      const params = new URLSearchParams({ pixel_id: pixelId, objective: this.objectiveValue })
      const res = await fetch(`/autoboosts/preflight_pixel.json?${params}`, {
        headers: { "Accept": "application/json" }
      })
      const json = await res.json()
      console.log("[campaign-creator] pixel preflight:", json)
      this._setPixelBlock(json.ok === false, json.message)
    } catch (e) {
      // Network hiccup: don't hard-block on a failed check.
      console.error("[campaign-creator] pixel preflight error:", e)
      this._setPixelBlock(false, null)
    }
    this.dispatch("changed")
  }

  objectiveNeedsPixel(objective) {
    return ["OUTCOME_SALES", "OUTCOME_LEADS"].includes(objective)
  }

  _setPixelBlock(blocked, message) {
    if (this.hasPixelBlockedTarget) this.pixelBlockedTarget.value = blocked ? "1" : ""
    if (!this.hasPixelErrorTarget) return
    if (blocked && message) {
      this.pixelErrorTarget.textContent = message
      this.pixelErrorTarget.classList.remove("hidden")
    } else {
      this.pixelErrorTarget.textContent = ""
      this.pixelErrorTarget.classList.add("hidden")
    }
  }

  async loadPixels() {
    const adAccountSelect = document.querySelector("[name='autoboost[fb_ad_account_id]']")
    if (!adAccountSelect || !adAccountSelect.value) return

    const adAccountId = adAccountSelect.value
    console.log("[campaign-creator] loadPixels for:", adAccountId)

    try {
      const response = await fetch(`/ad-accounts/${adAccountId}/pixels.json`)
      const json = await response.json()
      console.log("[campaign-creator] pixels loaded:", json.data?.length || 0)

      if (this.hasPixelSelectTarget && json.data) {
        this.pixelSelectTarget.innerHTML = '<option value="">Select a pixel...</option>'
        json.data.forEach(pixel => {
          const option = document.createElement("option")
          option.value = pixel.id
          option.textContent = pixel.name || pixel.id
          if (!pixel.eligible) {
            option.disabled = true
            option.textContent += " (other account)"
          }
          this.pixelSelectTarget.appendChild(option)
        })
      }
    } catch (error) {
      console.error("[campaign-creator] loadPixels error:", error)
    }
  }

  disconnect() {
    console.log("[campaign-creator] disconnect")
  }
}
